import {
  DETAIL_GROUPS,
  PRIMARY_STATUS,
  revealedGroups,
  toothPosition,
  viewerSideLabel,
  type StatusCategory,
  type StatusGroup,
  type ToothPosition,
} from '@toothsomatics/tooth-model';
import { useEffect, useRef, useState } from 'react';

import { CHART_FOOTNOTE } from '../../copy.js';
import { UploadSection } from './UploadSection.js';
import { EMPTY_ENTRY, toggleOption, type ImageDraft, type PositionEntry } from './types.js';
import './panel.css';

/**
 * What is known about one position.
 *
 * The exclusive rules and the reveal rules come from `packages/tooth-model` — the same functions
 * the API validates with. If this file held its own copy of "Not sure clears the rest", the browser
 * and the server would drift, and the server would be right while the person in front of the screen
 * was told otherwise.
 *
 * **Every detail section is always present**, collapsed, as in the client's mockup. An earlier
 * version rendered a section only once the status that warranted it was selected, which is tidier
 * and hides the shape of the exercise: someone looking at the panel could not tell that recording a
 * filling would go on to ask which material it was. A section whose status is selected opens
 * itself; the rest sit closed and can be opened by hand.
 */

const TYPE_LABEL: Readonly<Record<ToothPosition['type'], string>> = {
  incisor: 'Incisor',
  canine: 'Canine',
  premolar: 'Premolar',
  molar: 'Molar',
};

export interface StatusPanelProps {
  readonly toothNumber: number;
  readonly entry: PositionEntry | undefined;
  readonly onChange: (next: PositionEntry) => void;
  readonly images: readonly ImageDraft[];
  /** Gate 6 is scoped to the submission, so this is one answer for the whole exercise. */
  readonly photoConsent: boolean;
  readonly onPhotoConsentChange: (accepted: boolean) => void;
  readonly onAddImages: (files: readonly File[]) => void;
  readonly onRemoveImage: (id: string) => void;
}

export function StatusPanel({
  toothNumber,
  entry,
  onChange,
  images,
  photoConsent,
  onPhotoConsentChange,
  onAddImages,
  onRemoveImage,
}: StatusPanelProps) {
  const position = toothPosition(toothNumber);
  const current = entry ?? EMPTY_ENTRY;
  const primary = current.selections[PRIMARY_STATUS.category] ?? [];
  const revealed = new Set(revealedGroups(primary).map((group) => group.category));

  /**
   * Sections a person opened or closed by hand. Absent means "follow the selection", so a section
   * opens when its status is ticked and stays open afterwards only because the status is still
   * ticked — until someone decides otherwise, and then their decision wins.
   */
  const [overrides, setOverrides] = useState<Partial<Record<StatusCategory, boolean>>>({});

  /*
   * The panel is reused rather than remounted, so scroll position and open sections would otherwise
   * carry over: scroll to the bottom of a molar's options, click an incisor, and land halfway down a
   * shorter list with the first question already out of view.
   */
  const body = useRef<HTMLDivElement>(null);
  useEffect(() => {
    body.current?.scrollTo({ top: 0 });
    setOverrides({});
  }, [toothNumber]);

  /** The rule lives in `types.ts`, as a pure function with tests. See the note there. */
  const toggle = (category: StatusCategory, value: string, selected: boolean) => {
    onChange(toggleOption(current, category, value, selected));
  };

  return (
    /*
     * Deliberately not keyed on the position, and deliberately not animated on change. Keying it
     * remounted the whole panel on every click, replaying the entrance animation — the panel blinked
     * each time someone moved along the arch, which is most of what they do here.
     */
    <aside className="panel" aria-labelledby="panel-heading">
      {/* Stays put while the options scroll — you should always know which position you are in. */}
      <div className="panel__head">
        <p className="panel__eyebrow">Position {toothNumber}</p>
        <h2 className="panel__heading" id="panel-heading">
          {position.name}
        </h2>

        <dl className="panel__meta">
          <div>
            <dt>Arch</dt>
            <dd>{position.arch === 'upper' ? 'Upper' : 'Lower'}</dd>
          </div>
          <div>
            <dt>Side</dt>
            <dd>{viewerSideLabel(position.side)}</dd>
          </div>
          <div>
            <dt>Type</dt>
            <dd>{TYPE_LABEL[position.type]}</dd>
          </div>
        </dl>
      </div>

      <div className="panel__body" ref={body}>
        <Options
          group={PRIMARY_STATUS}
          selected={primary}
          onToggle={(value, checked) => toggle(PRIMARY_STATUS.category, value, checked)}
        />

        {DETAIL_GROUPS.map((group) => {
          const selected = current.selections[group.category] ?? [];
          const isRelevant = revealed.has(group.category);

          return (
            <details
              key={group.category}
              className={isRelevant ? 'detail detail--relevant' : 'detail'}
              open={overrides[group.category] ?? isRelevant}
              onToggle={(event) => {
                /*
                 * Read the DOM before updating state, not inside the updater.
                 *
                 * React calls the updater later, by which point `event.currentTarget` is null — so
                 * the override was never recorded, and because `open` is controlled the section
                 * snapped shut the instant it was opened.
                 */
                const isOpen = event.currentTarget.open;
                setOverrides((state) => ({ ...state, [group.category]: isOpen }));
              }}
            >
              <summary className="detail__summary">
                {group.title}
                {selected.length > 0 ? (
                  <span className="detail__count">{selected.length}</span>
                ) : null}
              </summary>

              <Options
                group={group}
                selected={selected}
                onToggle={(value, checked) => toggle(group.category, value, checked)}
              />
            </details>
          );
        })}

        <div className="panel__group">
          <label className="panel__label" htmlFor="position-note">
            Anything else you notice about this position
          </label>
          <textarea
            id="position-note"
            className="panel__note"
            rows={4}
            value={current.note}
            placeholder="A memory, a feeling, something you were told — whatever comes."
            onChange={(event) => onChange({ ...current, note: event.target.value })}
          />
        </div>

        <UploadSection
          toothNumber={toothNumber}
          images={images}
          consented={photoConsent}
          onConsentChange={onPhotoConsentChange}
          onAdd={onAddImages}
          onRemove={onRemoveImage}
        />

        <p className="panel__footnote">{CHART_FOOTNOTE}</p>
      </div>
    </aside>
  );
}

interface OptionsProps {
  readonly group: StatusGroup;
  readonly selected: readonly string[];
  readonly onToggle: (value: string, checked: boolean) => void;
}

function Options({ group, selected, onToggle }: OptionsProps) {
  return (
    <fieldset className="panel__group">
      {/* The primary group names itself; a detail group is already named by its summary. */}
      {group.revealedBy ? null : <legend className="panel__legend">{group.title}</legend>}
      {group.note ? <p className="panel__note-text">{group.note}</p> : null}

      {group.options.map((option) => (
        <label className="panel__option" key={option.value}>
          <input
            type="checkbox"
            checked={selected.includes(option.value)}
            onChange={(event) => onToggle(option.value, event.target.checked)}
          />
          <span>
            {option.label}
            {option.helpText ? <span className="panel__help">{option.helpText}</span> : null}
          </span>
        </label>
      ))}
    </fieldset>
  );
}
